import api from '../api';
import type { Claim, ClaimUpdateData } from '@/types/claim';

export interface ClaimFilters {
  search?: string;
  status?: string;
  brand?: number;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}

export interface ClaimsResponse {
  success: boolean;
  data: Claim[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface ClaimResponse {
  success: boolean;
  message?: string;
  data?: Claim;
}

export interface ClaimStatusUpdateData {
  status: 'approved' | 'rejected';
  rejection_reason?: string;
}

const API_BASE = '/admin/claims';

export const adminClaimService = {
  /**
   * Get all claims with filters and pagination
   */
  async getClaims(filters: ClaimFilters): Promise<ClaimsResponse> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.brand) params.append('brand', filters.brand.toString());
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);

    // Ensure page and per_page are valid numbers
    const page = filters.page && filters.page > 0 ? filters.page : 1;
    const perPage = filters.per_page && filters.per_page > 0 ? filters.per_page : 10;

    params.append('page', page.toString());
    params.append('per_page', perPage.toString());

    const response = await api.get(`${API_BASE}?${params.toString()}`);
    return response.data;
  },

  /**
   * Get single claim details
   */
  async getClaim(claimId: number): Promise<ClaimResponse> {
    const response = await api.get(`${API_BASE}/${claimId}`);
    return response.data;
  },

  /**
   * Update claim status (approve/reject)
   */
  async updateClaimStatus(
    claimId: number,
    data: ClaimStatusUpdateData
  ): Promise<ClaimResponse> {
    const response = await api.patch(`${API_BASE}/${claimId}/status`, data);
    return response.data;
  },

  /**
   * Update claim information (general update)
   */
  async updateClaim(
    claimId: number,
    data: ClaimUpdateData
  ): Promise<ClaimResponse> {
    const response = await api.put(`${API_BASE}/${claimId}`, data);
    return response.data;
  },

  /**
   * Delete claim (rejected claims only)
   */
  async deleteClaim(claimId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`${API_BASE}/${claimId}`);
    return response.data;
  },

  /**
   * Get all claims for export (no pagination)
   */
  async getClaimsForExport(filters: ClaimFilters): Promise<Claim[]> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.brand) params.append('brand', filters.brand.toString());
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);

    const response = await api.get(`${API_BASE}/export-data?${params.toString()}`);
    return response.data.data || [];
  }
};

